import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { getMenu } from '@core/helper';
import { LocalStorageHelper } from '@core/helper/local-storage.helper';
import { IMenuItem, ISubMenu } from '@core/models/interfaces/sidebar.interface';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { StoreService } from '@core/services/common/store.service';
import { hideSpinner, showSpinner } from '@core/store/features/spinner/spinner.action';
import { LOCAL_STORAGE_KEY, MODULE } from '@core/configs';
import { AuthService } from '@core/services/apis';

@Component({
  selector: 'app-sidebar-menu',
  templateUrl: './sidebar-menu.component.html',
  styleUrls: ['./sidebar-menu.component.scss']
})
export class SidebarMenuComponent implements OnInit {
  @Output() isShowMenu = new EventEmitter();
  menuItems: IMenuItem[] = [];
  showMenuItem = false;
  showMenu = false;

  menuExpand = new Subject();
  firstChild = '';

  constructor(
    private route: ActivatedRoute,
    private _router: Router,
    private _authService: AuthService,
    private _store: StoreService
  ) {
    this.menuExpand.pipe(debounceTime(300)).subscribe(() => {
      this.showMenu = !this.showMenu;
      this.isShowMenu.next(this.showMenu);
      
      if (this.showMenu) {
        // When expanding, show menu items immediately to sync with width animation
        this.showMenuItem = true;
      } else {
        // When collapsing, hide menu items immediately to sync with width animation
        this.showMenuItem = false;
      }
    });

    this._router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.firstChild = this.route.firstChild?.routeConfig?.path as string;
        this.checkActiveMenu();
      }
    });
  }

  ngOnInit(): void {
    this.getMenu();
  }

  getMenu() {

    this.menuItems = getMenu();
    this.menuExpand.next(this.showMenuItem);
    setTimeout(() => {
      this.firstChild = this.route.firstChild?.routeConfig?.path as string;
      this.checkActiveMenu();
    }, 300);
  }

  checkActiveMenu() {
    this.menuItems = this.menuItems.map(item => {
      item.icon = this.firstChild === item.link && item.icon === "careposi" ? `${item.link}-active` : item.icon as string;
      item.isActive = this.firstChild === item.link;
      item.expandIcon = this.firstChild === item.link ? 'up-gray' : 'up-white';
      const subLink = this.route.firstChild?.firstChild?.firstChild?.routeConfig?.path as string;
      item.children.forEach(child => {
        child.active = subLink === child.link;
        return child;
      });
      return item;
    });
  }

  onMenu() {
    this.menuExpand.next(this.showMenuItem);
  }

  onShowSubMenu(item: IMenuItem) {
    this.showMenuItem = true;
    if (!this.showMenu) {
      this.showMenu = true;
      this.isShowMenu.next(true);
    } else {
      item.expanded = !item.expanded;
      this.menuItems = this.menuItems.map(menu => {
        if (menu.isActive) {
          menu.expandIcon = menu.expanded ? 'up-gray' : 'down-gray';
        } else {
          menu.expandIcon = menu.expanded ? 'up-white' : 'down-white';
        }
        return menu;
      });
    }
  }

  onNavigate(item: IMenuItem, subMenu: ISubMenu) {
    // if (subMenu.link === "saucer-logs") return;
    if (subMenu.link === MODULE.saucerLog) {
      this.goToModuleLogin();
      return;
    }
    item.expandIcon = 'up-gray';
    LocalStorageHelper.removeQueryParams();
    const nextUrl = `${item.link}/${subMenu.link}`;
    LocalStorageHelper.setItem(LOCAL_STORAGE_KEY.previousURL, nextUrl);
    this._router.navigate([`/${item.link}/${subMenu.link}`]);
    this.menuItems = this.menuItems.map(menu => {
      menu.icon = item.link === menu.link ? `${menu.link}-active` : menu.link as string;
      menu.isActive = item.link === menu.link;
      if (item.link !== menu.link) {
        menu.expandIcon = menu.expandIcon?.replace('gray', 'white');
      }
      menu.children.forEach(child => {
        child.active = subMenu.link === child.link;
        if (child.active && child.link === "organization-management"){
          menu.icon = `${menu.link}-active`;
        }
        if (child.active && child.link === "map-management"){
          menu.icon = `${menu.link}-map`;
        }
        return child;
      });
      return menu;
    });
  }

  goToModuleLogin() {
    const accessToken = LocalStorageHelper.getAccessToken();
    const refreshToken = LocalStorageHelper.getRefreshToken();
    const isRefreshTokenExpired = this._authService.isRefreshTokenExpired;
    if (!isRefreshTokenExpired) {
      this._store.dispatch(showSpinner());
      this._authService.verifyRefreshToken(refreshToken ?? '').subscribe(
        data => {
          this._store.dispatch(hideSpinner());
          const { result } = data.result;
          if (result) {
            const urlAction = `?accessToken=${accessToken}&refreshToken=${refreshToken}&module=careposi-CONSOLE`;
            const url = environment.SAUCER_LOG_URL + urlAction;
            window.open(url, '_blank');
          } else {
            this._authService.logout().then();
          }
        },
        () => (this._store.dispatch(hideSpinner()))
      );
    } else {
      this._authService.logout().then();
    }
  }
}
